import React, { useLayoutEffect, useState } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiShoppingCart, FiShoppingBag, FiMenu, FiX } from 'react-icons/fi';
import { useCart, useQBCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import CartDrawer from '../Cart/CartDrawer';
import QBCartDrawer from '../Cart/QBCartDrawer';
import AccountDropdown from './AccountDropdown';
import LanguageToggle from '../Common/LanguageToggle';
import { getStoredLanguage, CUSTOMER_LANGUAGE_STORAGE_KEY } from '../../i18n';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: var(--header-height);
  background-color: #1C1511;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.4);
  z-index: 1000;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  padding: 0 1rem;
  max-width: var(--max-content-width);
  margin: 0 auto;

  /* Every button in the mobile Actions row (LanguageToggle's trigger,
     AccountDropdown's trigger, both cart IconButtons, MenuButton) has a
     global min-width/min-height: 44px floor (GlobalStyles.ts) that this
     fix must not fight -- so at narrow phone widths, Logo + all of Actions'
     content can't be shrunk below a hard floor. Dropping this padding to 0
     (paired with Actions' own gap:0 step-down below) reclaims just enough
     room for MenuButton to stay within the viewport at 360/375; see the
     LanguageToggleWrapper comment further down for the one additional step
     needed at 320px specifically, where even zero padding/gap isn't enough.
  */
  @media (max-width: 400px) {
    padding: 0;
  }

  @media (min-width: 768px) {
    padding: 0 2rem;
  }
`;

const Logo = styled(Link)`
  font-size: 1.5rem;
  font-weight: bold;
  color: #D4A017;
  text-decoration: none;
  font-family: Georgia, 'Times New Roman', serif;
  white-space: nowrap;

  @media (max-width: 767px) {
    font-size: 1.25rem;
  }

  @media (max-width: 480px) {
    font-size: 1.1rem;
  }

  @media (max-width: 360px) {
    font-size: 0.95rem;
  }
`;

const Nav = styled.nav<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  position: fixed;
  top: var(--header-height);
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #1C1511;
  flex-direction: column;
  padding: 2rem 1rem;
  border-top: 1px solid #E8DDD0;

  @media (min-width: 768px) {
    display: flex;
    position: static;
    flex-direction: row;
    padding: 0;
    background-color: transparent;
    align-items: center;
    gap: 2rem;
    border-top: none;
  }
`;

const NavLink = styled(Link)`
  padding: 1rem 0;
  color: #FAF8F3;
  text-decoration: none;
  font-size: 1.1rem;
  border-bottom: 1px solid #E8DDD0;

  &:hover {
    color: #D4A017;
  }

  @media (min-width: 768px) {
    padding: 0;
    border-bottom: none;
    font-size: 1rem;
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (max-width: 480px) {
    gap: 0.5rem;
  }

  @media (max-width: 400px) {
    gap: 0;
  }
`;

const IconButton = styled.button<{ $iconColor?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  color: ${props => props.$iconColor || '#FAF8F3'};
  background: transparent;
  border: none;
  cursor: pointer;
  position: relative;

  &:hover {
    color: #D4A017;
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

// LanguageToggle's own trigger button has a global min-width/min-height:
// 44px floor (GlobalStyles.ts `button` rule) that can't be shrunk, and its
// own file is off-limits for this fix. At 320px specifically, Logo + all 5
// Actions controls (LanguageToggle, AccountDropdown, both cart icons,
// MenuButton) can't simultaneously fit even with HeaderContent's padding
// and Actions' gap both already at zero (see those components' comments) --
// the 5 buttons' combined 44px-floor width alone (220px) plus Logo already
// exceeds 320px. j77-mobile-header-hamburger.spec.js pins LanguageToggle's
// globe icon as visible at 375px, so it can only be hidden narrower than
// that; 320px is the one tested width where hiding it is unavoidable to
// keep MenuButton itself on-screen (this fix's actual acceptance target).
const LanguageToggleWrapper = styled.div`
  @media (max-width: 320px) {
    display: none;
  }
`;

// Mirror image of LanguageToggleWrapper above: that wrapper hides LanguageToggle from the
// header's own Actions row at <=320px to reclaim room for MenuButton (see its comment), which
// otherwise leaves <=320px customers with no way to change language from the header at all. This
// renders a second, unchanged LanguageToggle instance inside the mobile hamburger Nav dropdown,
// visible ONLY at the same <=320px breakpoint (so it's never simultaneously visible alongside the
// header's own instance) and mounted ONLY while the dropdown is open (see the {isMenuOpen && ...}
// guard at its JSX call site) -- MenuButton is the only control that ever opens the dropdown and
// is itself CSS-hidden at >=768px, so this never mounts at desktop widths either.
const MobileNavLanguageToggle = styled.div`
  display: none;

  @media (max-width: 320px) {
    display: block;
    padding-top: 0.5rem;
  }
`;

const CartBadge = styled.span`
  position: absolute;
  top: 8px;
  right: 8px;
  background-color: #D4A017;
  color: #1C1511;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: bold;
`;

const MenuButton = styled(IconButton)`
  @media (min-width: 768px) {
    display: none;
  }
`;

const Header: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isQBCartOpen, setIsQBCartOpen] = useState(false);
  const { getCartCount, isCartOpen, openCart, closeCart } = useCart();
  const { qbCartCount } = useQBCart();
  const { isAuthenticated, user, logout } = useAuth();
  const cartItemCount = getCartCount();

  // The customer storefront and admin console share a single i18next
  // instance. If the admin surface most recently changed the shared
  // instance's active language (e.g. the user navigated storefront -> admin
  // -> back to storefront within one session), resync to the customer
  // surface's own stored preference on mount so admin language changes never
  // leak into the storefront.
  //
  // This must be a useLayoutEffect (not useEffect), and must force an
  // explicit re-render, rather than relying on react-i18next's own
  // `languageChanged` listener to trigger one. Header/AdminLayout mount
  // fresh on every cross-surface navigation (App.tsx renders them as
  // mutually exclusive Route trees) while both surfaces share one i18next
  // singleton -- so on mount, useTranslation()'s `t` initially resolves
  // against whatever language the *previous* surface last left the
  // singleton in. A plain useEffect only runs after the browser paints,
  // so the first frame would render in the wrong language (a visible
  // flash). useLayoutEffect runs synchronously after DOM mutations but
  // before paint, so correcting the language here prevents the wrong
  // language from ever being painted. react-i18next's own
  // `languageChanged` listener is itself bound from a plain useEffect
  // inside the library, so it is NOT yet registered when this
  // useLayoutEffect synchronously calls i18n.changeLanguage() during the
  // very first mount -- relying on that listener to re-render us would
  // silently leave this surface stuck on the wrong language instead of
  // just flashing it. The explicit forceLanguageResyncRerender() call
  // below is what actually schedules the corrected re-render.
  //
  // This also relies on i18n.changeLanguage() resolving synchronously,
  // which holds today because frontend/src/i18n/index.ts passes
  // `resources` inline at init() with no backend/lazy-loading plugin. If
  // a lazy-loading i18n backend is ever introduced, this fix needs
  // revisiting.
  const [, forceLanguageResyncRerender] = useState(0);
  useLayoutEffect(() => {
    const storedLanguage = getStoredLanguage(CUSTOMER_LANGUAGE_STORAGE_KEY);
    if (storedLanguage !== i18n.language) {
      i18n.changeLanguage(storedLanguage);
      forceLanguageResyncRerender(n => n + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <HeaderContainer>
      <HeaderContent>
        <Logo to="/">{t('common.brandName')}</Logo>

        <Nav $isOpen={isMenuOpen}>
          <NavLink to="/quick-bites" onClick={() => setIsMenuOpen(false)}>
            {t('nav.quickBites')}
          </NavLink>
          <NavLink to="/custom-cakes" onClick={() => setIsMenuOpen(false)}>
            {t('nav.customCakes')}
          </NavLink>
          {isMenuOpen && (
            <MobileNavLanguageToggle>
              <LanguageToggle color="#FAF8F3" storageKey={CUSTOMER_LANGUAGE_STORAGE_KEY} />
            </MobileNavLanguageToggle>
          )}
        </Nav>

        <Actions>
          <LanguageToggleWrapper>
            <LanguageToggle color="#FAF8F3" storageKey={CUSTOMER_LANGUAGE_STORAGE_KEY} hideBadgeOnMobile />
          </LanguageToggleWrapper>

          <AccountDropdown
            isAuthenticated={isAuthenticated}
            user={user}
            onLogout={handleLogout}
            color="#FAF8F3"
          />

          <IconButton onClick={() => setIsQBCartOpen(true)} aria-label='Open Quick Bites cart' $iconColor='#1A9E8F'>
            <FiShoppingBag />
            {qbCartCount > 0 && <CartBadge>{qbCartCount}</CartBadge>}
          </IconButton>

          <IconButton onClick={openCart} aria-label='Open Custom Cakes cart'>
            <FiShoppingCart />
            {cartItemCount > 0 && <CartBadge>{cartItemCount}</CartBadge>}
          </IconButton>

          <MenuButton onClick={toggleMenu}>
            {isMenuOpen ? <FiX /> : <FiMenu />}
          </MenuButton>
        </Actions>
      </HeaderContent>
      <CartDrawer isOpen={isCartOpen} onClose={closeCart} />
      <QBCartDrawer isOpen={isQBCartOpen} onClose={() => setIsQBCartOpen(false)} />
    </HeaderContainer>
  );
};

export default Header;
